import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Waves } from "lucide-react";
import { CONTACT } from "@/lib/scroll";

const QUOTES = [
  { text: "The only way to make sense out of change is to plunge into it, move with it, and join the dance.", author: "Alan Watts" },
  { text: "It's the relationship that heals.", author: "Irvin D. Yalom" },
  { text: "When we are no longer able to change a situation, we are challenged to change ourselves.", author: "Viktor Frankl" },
  { text: "The curious paradox is that when I accept myself just as I am, then I change.", author: "Carl Rogers" },
  { text: "Feelings come and go like clouds in a windy sky. Conscious breathing is my anchor.", author: "Thich Nhat Hanh" },
];

export const Footer = () => {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % QUOTES.length), 7000);
    return () => clearInterval(t);
  }, []);

  const q = QUOTES[idx];

  return (
    <footer data-testid="site-footer" className="bg-pawc-ink text-pawc-sand">
      <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
        <div className="min-h-[120px] flex items-center justify-center text-center mb-16">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={idx}
              data-testid="footer-quote"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-2xl"
            >
              <p className="font-serif italic text-2xl md:text-3xl text-pawc-mist/90 leading-snug">
                "{q.text}"
              </p>
              <cite className="not-italic text-xs tracking-[0.25em] uppercase text-pawc-sand/50 mt-4 block">
                {q.author}
              </cite>
            </motion.blockquote>
          </AnimatePresence>
        </div>

        <div className="border-t border-pawc-sand/10 pt-12 grid md:grid-cols-3 gap-10 text-sm">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <span className="w-14 h-14 rounded-2xl bg-pawc-sand flex items-center justify-center p-1.5">
                <img
                  src="/images/logo-mark.png"
                  alt="Yielding logo, water curling around a smooth stone"
                  className="w-full h-full object-contain"
                />
              </span>
              <span>
                <span className="font-serif text-lg block">{CONTACT.brand}</span>
                <span className="text-[10px] tracking-[0.3em] uppercase text-pawc-mist/60">
                  {CONTACT.brandSub}
                </span>
              </span>
            </div>
            <p className="text-pawc-mist/60 italic font-serif text-base">
              A narrative-informed psychology practice
            </p>
          </div>
          <div className="text-pawc-mist/70 space-y-2">
            <p className="text-pawc-sand font-medium mb-3">{CONTACT.name}</p>
            <p>{CONTACT.title}</p>
            <p>HPCSA Reg. No: {CONTACT.hpcsa}</p>
            <p>Practice No: {CONTACT.practiceNo}</p>
            <p className="pt-2 text-pawc-mist/50">
              {CONTACT.practice}: {CONTACT.pawcDays}
            </p>
            <p className="text-pawc-mist/50">Wits University: {CONTACT.witsDays}</p>
          </div>
          <div className="text-pawc-mist/70 space-y-2">
            <p className="text-pawc-sand font-medium mb-3">Visit</p>
            <p>{CONTACT.address}</p>
            <p>{CONTACT.phone}</p>
            <p>{CONTACT.email}</p>
          </div>
        </div>

        <div className="border-t border-pawc-sand/10 mt-12 pt-8 flex flex-col md:flex-row justify-between gap-4 text-xs text-pawc-mist/40">
          <p>© {new Date().getFullYear()} {CONTACT.brand}: {CONTACT.brandSub} · {CONTACT.name}. All rights reserved.</p>
          <a data-testid="footer-admin-link" href="/admin" className="hover:text-pawc-mist/70 transition-colors duration-300">
            Practice login
          </a>
        </div>
      </div>
    </footer>
  );
};
